import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertTriangle, Plus } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Textarea } from "../../components/ui/textarea";
import { API_URL, getAuthHeaders } from "../../lib/utils";
import { useApiMessages } from "../../hooks/useApiMessages";
import { useVisitorScope } from "../../components/visitors/useVisitorScope";
import {
  IncidentStatusBadge,
  NoFacilitiesNotice,
  PageHeader,
  ScopeFilters,
  SeverityBadge,
} from "../../components/visitors/VisitorShared";
import { INCIDENT_TYPES, IncidentDialog, SEVERITIES } from "../../components/visitors/VisitorForms";
import { VisitDetailDialog } from "../../components/visitors/VisitDetailDialog";
import { fmtDateTime, visitorApi } from "../../components/visitors/visitorUtils";

const STATUSES = ["open", "in_progress", "resolved", "closed"];

export default function VisitIncidents() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const scope = useVisitorScope();
  const [incidents, setIncidents] = useState([]);
  const [status, setStatus] = useState("open,in_progress");
  const [severity, setSeverity] = useState("all");
  const [type, setType] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [users, setUsers] = useState([]);
  const [update, setUpdate] = useState({});
  const [visitId, setVisitId] = useState(null);

  const load = useCallback(async () => {
    try {
      setIncidents(
        await visitorApi.get("/incidents", { ...scope.params, status, severity, incident_type: type })
      );
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.loadError"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.companyId, scope.facilityId, status, severity, type]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!scope.isManager) return;
    axios
      .get(`${API_URL}/users`, { headers: getAuthHeaders() })
      .then((r) => setUsers(r.data.filter((u) => u.is_active)))
      .catch(() => setUsers([]));
  }, [scope.isManager]);

  const openIncident = async (inc) => {
    try {
      const full = await visitorApi.get(`/incidents/${inc.id}`);
      setSelected(full);
      setUpdate({ status: full.status, severity: full.severity, assigned_to: full.assigned_to || "", note: "" });
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.loadError"));
    }
  };

  const saveUpdate = async () => {
    const payload = { note: update.note || null };
    if (scope.isManager) {
      if (update.status !== selected.status) payload.status = update.status;
      if (update.severity !== selected.severity) payload.severity = update.severity;
      if (update.assigned_to && update.assigned_to !== selected.assigned_to) payload.assigned_to = update.assigned_to;
    }
    try {
      const updated = await visitorApi.put(`/incidents/${selected.id}`, payload);
      toast.success(t("visitors.incidents.updated"));
      setSelected(updated);
      setUpdate((u) => ({ ...u, note: "" }));
      load();
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.saveError"));
    }
  };

  const companyUsers = users.filter((u) => !selected || u.company_id === selected.company_id);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("visitors.incidents.title")}
        subtitle={t("visitors.incidents.subtitle")}
        actions={
          <Button onClick={() => setCreateOpen(true)} disabled={scope.facilities.length === 0}>
            <Plus className="h-4 w-4 mr-2" />
            {t("visitors.incidents.newIncident")}
          </Button>
        }
      />

      <ScopeFilters scope={scope}>
        <div>
          <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.common.status")}</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open,in_progress">{t("visitors.incidents.active")}</SelectItem>
              <SelectItem value="all">{t("visitors.preauths.filters.all")}</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`visitors.incidentStatus.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.incidents.severity")}</Label>
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("visitors.preauths.filters.all")}</SelectItem>
              {SEVERITIES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`visitors.severity.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.incidents.type")}</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("visitors.preauths.filters.all")}</SelectItem>
              {INCIDENT_TYPES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`visitors.incidentTypes.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </ScopeFilters>
      {scope.loaded && scope.facilities.length === 0 && <NoFacilitiesNotice isManager={scope.isManager} />}

      <Card>
        <CardContent className="p-0 sm:p-2">
          {incidents.length === 0 ? (
            <p className="text-center py-10 text-slate-500">{t("visitors.incidents.none")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("visitors.incidents.type")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("visitors.incidents.description")}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t("visitors.common.facility")}</TableHead>
                  <TableHead>{t("visitors.incidents.severity")}</TableHead>
                  <TableHead>{t("visitors.common.status")}</TableHead>
                  <TableHead className="hidden sm:table-cell">{t("visitors.common.date")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {incidents.map((inc) => (
                  <TableRow key={inc.id} className="cursor-pointer" onClick={() => openIncident(inc)}>
                    <TableCell>
                      <p className="font-medium">{t(`visitors.incidentTypes.${inc.incident_type}`)}</p>
                      <p className="text-xs text-slate-500">{inc.visitor_name || "—"}</p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell max-w-xs truncate">{inc.description}</TableCell>
                    <TableCell className="hidden lg:table-cell">{inc.facility_name}</TableCell>
                    <TableCell>
                      <SeverityBadge severity={inc.severity} />
                    </TableCell>
                    <TableCell>
                      <IncidentStatusBadge status={inc.status} />
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm">{fmtDateTime(inc.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <IncidentDialog open={createOpen} onOpenChange={setCreateOpen} scope={scope} onDone={load} />

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {selected && t(`visitors.incidentTypes.${selected.incident_type}`)}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <SeverityBadge severity={selected.severity} />
                <IncidentStatusBadge status={selected.status} />
              </div>
              <p className="whitespace-pre-wrap">{selected.description}</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <p>
                  <span className="text-slate-500">{t("visitors.common.facility")}:</span> {selected.facility_name}
                </p>
                <p>
                  <span className="text-slate-500">{t("visitors.common.visitor")}:</span> {selected.visitor_name || "—"}
                </p>
                <p>
                  <span className="text-slate-500">{t("visitors.incidents.reportedBy")}:</span> {selected.reported_by_name}
                </p>
                <p>
                  <span className="text-slate-500">{t("visitors.common.date")}:</span> {fmtDateTime(selected.created_at)}
                </p>
                <p>
                  <span className="text-slate-500">{t("visitors.incidents.assignedTo")}:</span> {selected.assigned_to_name || "—"}
                </p>
                {selected.workday_date && (
                  <p>
                    <span className="text-slate-500">{t("visitors.common.shift")}:</span> {selected.workday_date}
                  </p>
                )}
              </div>
              {selected.visit_id && (
                <Button variant="link" className="px-0" onClick={() => setVisitId(selected.visit_id)}>
                  {t("visitors.incidents.linkedVisit")}
                </Button>
              )}
              {selected.photos?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selected.photos.map((p, i) => (
                    <a key={i} href={p} target="_blank" rel="noreferrer">
                      <img src={p} alt="" className="h-28 w-28 rounded-lg object-cover border" />
                    </a>
                  ))}
                </div>
              )}

              <section>
                <h4 className="font-semibold mb-2">{t("visitors.incidents.followUps")}</h4>
                {selected.follow_ups?.length ? (
                  <ol className="border-l-2 border-slate-200 ml-2 space-y-2">
                    {selected.follow_ups.map((f) => (
                      <li key={f.id} className="ml-4 text-sm">
                        <span className="text-slate-500">
                          {fmtDateTime(f.at)} · {f.user_name}
                        </span>
                        {f.changes &&
                          Object.entries(f.changes).map(([k, v]) => (
                            <p key={k} className="text-slate-600">
                              {t(`visitors.incidents.changeFields.${k}`)}:{" "}
                              {Array.isArray(v)
                                ? `${t(`visitors.${k === "status" ? "incidentStatus" : "severity"}.${v[0]}`)} → ${t(
                                    `visitors.${k === "status" ? "incidentStatus" : "severity"}.${v[1]}`
                                  )}`
                                : v}
                            </p>
                          ))}
                        {f.note && <p>{f.note}</p>}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-sm text-slate-500">—</p>
                )}
              </section>

              <section className="rounded-lg border p-4 space-y-3">
                {scope.isManager && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="mb-1 block">{t("visitors.common.status")}</Label>
                      <Select value={update.status} onValueChange={(v) => setUpdate({ ...update, status: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {t(`visitors.incidentStatus.${s}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="mb-1 block">{t("visitors.incidents.severity")}</Label>
                      <Select value={update.severity} onValueChange={(v) => setUpdate({ ...update, severity: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SEVERITIES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {t(`visitors.severity.${s}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="mb-1 block">{t("visitors.incidents.assignTo")}</Label>
                      <Select value={update.assigned_to || "__none"} onValueChange={(v) => setUpdate({ ...update, assigned_to: v === "__none" ? "" : v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">{t("visitors.common.none")}</SelectItem>
                          {companyUsers.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name} · {t(`roles.${u.role}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <div>
                  <Label className="mb-1 block">{t("visitors.incidents.addNote")}</Label>
                  <Textarea rows={2} value={update.note || ""} onChange={(e) => setUpdate({ ...update, note: e.target.value })} />
                </div>
              </section>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>
              {t("visitors.common.close")}
            </Button>
            <Button onClick={saveUpdate}>{t("visitors.common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VisitDetailDialog visitId={visitId} open={!!visitId} onOpenChange={(o) => !o && setVisitId(null)} scope={scope} />
    </div>
  );
}
